import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CoachMark, { useElement, useElements } from "./CoachMark";
import GettingStartedPopup, { GuideDock, TargetRing } from "./GettingStartedPopup";
import { fetchFirstRunGuide, sendFirstRunGuideEvent } from "../../store/slices/firstRunGuideSlice";
import { updateNavigationTab } from "../../store/slices/candidateManagementSlice";
import { removeActiveFilter, updateCurrentJobRole } from "../../store/slices/filterOptionsSlice";

// Calls that can move the guide on (adding candidates, scheduling interviews),
// from whichever page makes them. When one succeeds the guide refreshes at once.
const PROGRESS_CALL = /\/(store_candidate_data(_v2)?|schedule_interviews|save_draft_candidate|Bulkupload)(\?|$)/;
// Scheduling from Screen a Resume reloads that page two seconds later; the
// guide stays hidden until then so the next step's pop-up shows only once.
const RELOADS_AFTER = /\/store_candidate_data(\?|$)/;

// Coach marks inside Screen a Resume, before a resume is screened. Once it is,
// the Schedule Interview button appears and the pop-up takes over.
// doneFlag: attribute on the job role field that AnalyseResume sets to "true"
// once the user has done that step; the guide then moves on without "Next".
const SCREEN_RESUME_MARKS = [
  {
    target: "select-job-role",
    doneFlag: "data-guide-job-selected",
    title: "Select a job role",
    body: "Pick the job role this resume is for. The candidate is scored against it.",
    placement: "top",
    // Out of the way while the job role list is open
    hideWhenExpanded: true,
  },
  {
    target: "upload-resume",
    doneFlag: "data-guide-resume-added",
    title: "Upload a resume",
    body: "Drop in a PDF or DOCX. GenieHire reads it and scores how well it matches the job role.",
    placement: "top",
  },
];

// Coach marks on Settings -> Company Profile, the only mandatory things there:
// the website, the About Company description, then Save. Saving completes the
// guide's company_profile step, which ends these marks.
const SETTINGS_MARKS = [
  {
    target: "company-website",
    title: "Add your website",
    body: "Enter your company website. It's required, and lets GenieHire write your company description for you.",
    placement: "bottom",
  },
  {
    target: "company-about",
    title: "Describe your company",
    body: "Write a few lines about your company, or press Generate with AI. Candidates see this before they apply.",
    placement: "top",
  },
  {
    target: "save-company-profile",
    title: "Save your profile",
    body: "Save to finish this step. You can change it any time.",
    placement: "left",
  },
];

const tab = (name) => `[data-guide="${name}"]`;

const isJobBuilder = (path) => path.includes("jd-selection") || path.includes("create-jd");

// The pop-up opens once per sign-in for each place it points to: at login, and
// again whenever the guide moves on. Closing it leaves the target ringed.
const CLOSED_KEY = "gh_guide_closed";
const signInId = () => {
  try {
    return String(jwtDecode(sessionStorage.getItem("accessToken")).auth_time || "");
  } catch {
    return "";
  }
};
const readClosed = () => {
  try {
    return sessionStorage.getItem(CLOSED_KEY) || "";
  } catch {
    return "";
  }
};

// True once the page's content has finished loading: no app loader spinner in
// the main area for two checks in a row. Resets on every navigation, so the
// pop-up waits for e.g. the Job Roles list rather than opening over its loader.
// The result is stored with the path it was measured on: resetting it in an
// effect left the previous page's "settled" in place for the first render on
// the new page, which flashed the pop-up open for a frame.
const usePageSettled = (pathname) => {
  const [settledPath, setSettledPath] = useState(null);
  useEffect(() => {
    let quiet = 0;
    const id = setInterval(() => {
      quiet = document.querySelector("main .loader") ? 0 : quiet + 1;
      if (quiet >= 2) {
        setSettledPath(pathname);
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, [pathname]);
  return settledPath === pathname;
};

// An element's attributes, kept current the moment they change (no polling),
// so the guide reacts in the same instant the page does.
const WATCHED = ["aria-expanded", "data-guide-job-selected", "data-guide-resume-added"];
const useWatchedAttributes = (el) => {
  const [attrs, setAttrs] = useState({});
  useEffect(() => {
    if (!el) {
      setAttrs({});
      return undefined;
    }
    const read = () => setAttrs(Object.fromEntries(WATCHED.map((name) => [name, el.getAttribute(name)])));
    read();
    const observer = new MutationObserver(read);
    observer.observe(el, { attributes: true, attributeFilter: WATCHED });
    return () => observer.disconnect();
  }, [el]);
  return attrs;
};

const FirstRunGuide = ({ suppressed }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { visible, step, steps, openRequest } = useSelector(
    (state) => state.firstRunGuide,
  );
  const [markIndex, setMarkIndex] = useState(0);
  const [closed, setClosed] = useState(readClosed);
  // Docked guide opened into the full pop-up by the user
  const [dockOpen, setDockOpen] = useState(false);
  const [holdForReload, setHoldForReload] = useState(false);
  const pageSettled = usePageSettled(pathname);

  useEffect(() => {
    dispatch(fetchFirstRunGuide());
    // Navigating away instead of reloading also ends the hold
    setHoldForReload(false);
  }, [dispatch, pathname]);

  // The "complete your profile" form some accounts get first can fill in the
  // company profile; check again once it closes.
  useEffect(() => {
    if (!suppressed) dispatch(fetchFirstRunGuide());
  }, [dispatch, suppressed]);

  useEffect(() => {
    const id = axios.interceptors.response.use((response) => {
      const url = response?.config?.url || "";
      if (PROGRESS_CALL.test(url)) dispatch(fetchFirstRunGuide());
      if (RELOADS_AFTER.test(url) && window.location.pathname.includes("checkresume")) setHoldForReload(true);
      return response;
    });
    return () => axios.interceptors.response.eject(id);
  }, [dispatch]);

  const onSettings = pathname.includes("/configurations");
  const onCheckResume = pathname.includes("checkresume");
  const onScreened = pathname.includes("/schedule-interview");
  const onInterviews = pathname.includes("/candidate-management");

  // Clicking the page's Schedule Interview button opens the scheduling modal.
  // The guide gets out of the way at once (on the click, not on the next poll)
  // and stays hidden while that modal is open. If the click does not open it --
  // a required field is missing and the page shows a warning -- the guide
  // comes back after a moment.
  const [scheduling, setScheduling] = useState(false);
  const scheduleModal = useElement(scheduling ? tab("schedule-modal") : null);
  const modalSeen = useRef(false);
  useEffect(() => {
    if (!scheduling) {
      modalSeen.current = false;
      return undefined;
    }
    if (scheduleModal) {
      modalSeen.current = true;
      return undefined;
    }
    if (modalSeen.current) {
      // Modal closed without scheduling: back to the docked guide
      setScheduling(false);
      return undefined;
    }
    const id = setTimeout(() => {
      if (!modalSeen.current) setScheduling(false);
    }, 800);
    return () => clearTimeout(id);
  }, [scheduling, scheduleModal]);
  useEffect(() => setScheduling(false), [pathname]);

  const active = visible && !suppressed && !holdForReload && !scheduling && !isJobBuilder(pathname);

  // On Screen a Resume, this button appears once a resume has been screened
  const scheduleButton = useElement(
    active && onCheckResume && (step === "screen_resume" || step === "schedule_interview")
      ? tab("schedule-interview")
      : null,
  );
  useEffect(() => {
    if (!scheduleButton) return undefined;
    const onClick = () => {
      setDockOpen(false);
      setScheduling(true);
    };
    scheduleButton.addEventListener("click", onClick);
    return () => scheduleButton.removeEventListener("click", onClick);
  }, [scheduleButton]);

  const openCandidateTab = useCallback(
    (navTab, path) => {
      // Same state reset the header tabs do
      dispatch(updateCurrentJobRole("All Job Roles"));
      dispatch(removeActiveFilter("JOB_ROLE"));
      dispatch(updateNavigationTab(navTab));
      navigate(path);
    },
    [dispatch, navigate],
  );

  // What the pop-up points at here and now, or null where the page itself
  // guides (coach marks) or the user is already where they need to be.
  let plan = null;
  if (active) {
    if (scheduleButton && (step === "screen_resume" || step === "schedule_interview")) {
      // The recruiter is reading the screening results here: no dim or pop-up
      // over them, just a pulsing ring on the button and the corner dock. No
      // button in the pop-up: the ringed button on the page is the way on.
      plan = {
        key: "schedule:button",
        dock: true,
        activeKey: "schedule_interview",
        steps: { ...steps, screen_resume: true },
        selector: tab("schedule-interview"),
      };
    } else if (step === "company_profile" && !onSettings) {
      // No button: the line points to Settings in the sidebar
      plan = { key: "company_profile", selector: tab("settings") };
    } else if (step === "create_job") {
      // No button here: the ringed Create Job Role tab is the way in
      plan = { key: "create_job", selector: tab("create-job") };
    } else if (step === "screen_resume" && !onCheckResume) {
      // No button here: the ringed Screen Resume tab is the way in
      plan = { key: "screen_resume", selector: tab("screen-resume") };
    } else if (step === "schedule_interview" && !onCheckResume && !onScreened) {
      plan = {
        key: "schedule_interview",
        selector: tab("screened-candidates"),
        ctaLabel: "Open Screened Candidates",
        onCta: () => openCandidateTab("SOURCED", "/org-admin/schedule-interview"),
      };
    } else if (step === "track_interview" && !onInterviews) {
      // No button: the ringed Interview Management tab is the way in
      plan = { key: "track_interview", selector: tab("interview-management") };
    }
  }

  // The Help page's Start Guide button: show the pop-up again even if it was
  // closed for this step earlier in the session.
  useEffect(() => {
    if (!openRequest) return;
    try {
      sessionStorage.removeItem(CLOSED_KEY);
    } catch {
      /* storage blocked */
    }
    setClosed("");
    setDockOpen(true);
  }, [openRequest]);

  // Reaching Interview Management is the last step: the guide ends there
  useEffect(() => {
    if (active && step === "track_interview" && onInterviews) dispatch(sendFirstRunGuideEvent("completed"));
  }, [dispatch, active, step, onInterviews]);

  const targets = useElements(plan ? plan.selector : null);
  const popupKey = plan ? `${signInId()}|${plan.key}` : "";
  const popupDue = !!plan && closed !== popupKey;
  const popupOpen = popupDue && pageSettled;
  useEffect(() => setDockOpen(false), [popupKey]);

  const close = useCallback(() => {
    try {
      sessionStorage.setItem(CLOSED_KEY, popupKey);
    } catch {
      /* storage blocked: it just closes for this page view */
    }
    setClosed(popupKey);
  }, [popupKey]);

  const skip = useCallback(() => {
    dispatch(sendFirstRunGuideEvent("dismissed"));
    toast.info("Guide skipped. You can restart it any time from Help in the sidebar.");
  }, [dispatch]);

  // Screen a Resume: doing a step yourself (picking a job role, adding a
  // resume) counts the same as pressing Next.
  const showMarks = active && step === "screen_resume" && onCheckResume && !scheduleButton;
  const jobRoleField = useElement(showMarks ? tab("select-job-role") : null);
  const fieldAttrs = useWatchedAttributes(jobRoleField);
  const jobRoleListOpen = fieldAttrs["aria-expanded"] === "true";
  useEffect(() => {
    if (step !== "screen_resume") setMarkIndex(0);
  }, [step]);
  // The mark to show: past every step already done on the page, or skipped
  // with Next. Worked out on render, so it moves on in the same instant.
  let currentMark = markIndex;
  while (
    currentMark < SCREEN_RESUME_MARKS.length &&
    fieldAttrs[SCREEN_RESUME_MARKS[currentMark].doneFlag] === "true"
  ) {
    currentMark += 1;
  }

  // Settings: Website, then About Company, then Save. Next always moves on;
  // the form itself says what is missing if they save with a field empty.
  const showSettingsMarks = active && step === "company_profile" && onSettings;
  const websiteField = useElement(showSettingsMarks ? tab("company-website") : null);
  const aboutField = useElement(showSettingsMarks ? tab("company-about") : null);
  const [settingsMark, setSettingsMark] = useState(0);
  useEffect(() => {
    if (!showSettingsMarks) setSettingsMark(0);
  }, [showSettingsMarks]);

  if (!active) return null;

  if (plan?.dock) {
    if (!dockOpen) {
      return (
        <>
          <TargetRing targets={targets} pulse />
          <GuideDock activeKey={plan.activeKey || step} onOpen={() => setDockOpen(true)} />
        </>
      );
    }
    return (
      <GettingStartedPopup
        steps={plan.steps || steps}
        activeKey={plan.activeKey || step}
        targets={targets}
        onClose={() => setDockOpen(false)}
        onSkip={skip}
        // The screening results stay readable and usable behind the card
        blockPage={false}
      />
    );
  }

  if (plan) {
    // Due but the page is still loading: show nothing yet, so the ring, the
    // line and the pop-up all appear together once it has loaded
    if (popupDue && !pageSettled) return null;
    if (!popupOpen) return <TargetRing targets={targets} />;
    return (
      <GettingStartedPopup
        steps={plan.steps || steps}
        activeKey={plan.activeKey || step}
        targets={targets}
        ctaLabel={plan.ctaLabel}
        onCta={() => {
          close();
          plan.onCta();
        }}
        onClose={close}
        onSkip={skip}
      />
    );
  }

  if (showSettingsMarks && websiteField && aboutField && pageSettled) {
    const mark = SETTINGS_MARKS[settingsMark];
    const first = settingsMark === 0;
    const last = settingsMark === SETTINGS_MARKS.length - 1;
    return (
      <CoachMark
        key={mark.target}
        {...mark}
        progress={`Step ${settingsMark + 1} of ${SETTINGS_MARKS.length}`}
        // Settings stays fully usable: they can go back and edit the website
        // while the guide points at a later step
        blockPage={false}
        primaryLabel={last ? "Back" : "Next"}
        onPrimary={() => setSettingsMark(last ? settingsMark - 1 : settingsMark + 1)}
        secondaryLabel={first || last ? undefined : "Back"}
        onSecondary={() => setSettingsMark(settingsMark - 1)}
        onSkip={skip}
      />
    );
  }

  if (showMarks && currentMark < SCREEN_RESUME_MARKS.length) {
    const { hideWhenExpanded, ...mark } = SCREEN_RESUME_MARKS[currentMark];
    if (hideWhenExpanded && jobRoleListOpen) return null;
    return (
      <CoachMark
        key={mark.target}
        {...mark}
        progress={`Step ${currentMark + 1} of ${SCREEN_RESUME_MARKS.length}`}
        // The page stays usable: they can still pick a job role while the
        // guide points at the upload box
        blockPage={false}
        primaryLabel="Next"
        onPrimary={() => setMarkIndex(currentMark + 1)}
        onSkip={skip}
      />
    );
  }

  return null;
};

export default FirstRunGuide;
